// Renderer for Space Invaders
export class Renderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: true });
    this.setupCanvas();
  }

  setupCanvas() {
    // Make drawing crisp on high-dpi
    const fitToContainer = () => {
      const ratio = window.devicePixelRatio || 1;
      const w = this.canvas.clientWidth;
      const h = this.canvas.clientHeight;
      this.canvas.width = Math.floor(w * ratio);
      this.canvas.height = Math.floor(h * ratio);
      this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    };
    
    // Wait for canvas to be properly sized
    setTimeout(fitToContainer, 0);
    window.addEventListener('resize', fitToContainer);
  }

  roundRect(ctx, x, y, w, h, r, fill, stroke) {
    if (typeof r === 'undefined') r = 5;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
    if (fill) ctx.fill();
    if (stroke) ctx.stroke();
  }

  glowRect(x, y, w, h, rx, ry, tint) {
    this.ctx.save();
    this.ctx.fillStyle = tint.fill;
    this.ctx.strokeStyle = tint.stroke;
    this.ctx.lineWidth = 1.2;
    this.ctx.shadowColor = tint.glow;
    this.ctx.shadowBlur = 20;
    this.roundRect(this.ctx, x, y, w, h, 4, true, true);
    this.ctx.restore();
    
    // Inner subtle highlight
    this.ctx.save();
    this.ctx.globalCompositeOperation = 'lighter';
    this.ctx.fillStyle = 'rgba(255,255,255,0.02)';
    this.roundRect(this.ctx, x + 2, y + 2, w - 4, h - 4, 3, true, false);
    this.ctx.restore();
  }

  drawBackground() {
    const W = this.canvas.width / (window.devicePixelRatio || 1);
    const H = this.canvas.height / (window.devicePixelRatio || 1);
    
    // Simple dark background
    this.ctx.fillStyle = '#0f172a';
    this.ctx.fillRect(0, 0, W, H);
  }

  drawBarriers(barriers) {
    barriers.forEach(bar => {
      const m = bar.mask;
      for (let r = 0; r < m.rows; r++) {
        for (let c = 0; c < m.cols; c++) {
          if (!m.grid[r][c]) continue;
          const bx = bar.x + c * m.cellW;
          const by = bar.y + r * m.cellH;
          
          this.ctx.fillStyle = 'rgba(140, 220, 255, 0.3)';
          this.ctx.fillRect(bx, by, m.cellW - 1, m.cellH - 1);
        }
      }
    });
  }

  drawEnemies(enemies) {
    enemies.forEach(e => {
      if (!e.alive) return;
      
      // Farlige fiendefarger
      const colors = ['#a78bfa', '#60a5fa', '#fca5a5'];
      const color = colors[e.tint];
      
      // Farlig edderkopp-lignende form (ingen glød, bare form)
      this.ctx.fillStyle = color;
      this.ctx.beginPath();
      
      // Toppseksjon (hode)
      this.ctx.moveTo(e.x + e.w * 0.3, e.y);
      this.ctx.lineTo(e.x + e.w * 0.2, e.y + e.h * 0.2);
      this.ctx.lineTo(e.x + e.w * 0.1, e.y + e.h * 0.3);
      this.ctx.lineTo(e.x + e.w * 0.15, e.y + e.h * 0.4);
      this.ctx.lineTo(e.x + e.w * 0.1, e.y + e.h * 0.5);
      this.ctx.lineTo(e.x + e.w * 0.15, e.y + e.h * 0.6);
      this.ctx.lineTo(e.x + e.w * 0.1, e.y + e.h * 0.7);
      this.ctx.lineTo(e.x + e.w * 0.2, e.y + e.h * 0.8);
      this.ctx.lineTo(e.x + e.w * 0.3, e.y + e.h);
      
      // Høyre side
      this.ctx.lineTo(e.x + e.w * 0.7, e.y + e.h);
      this.ctx.lineTo(e.x + e.w * 0.8, e.y + e.h * 0.8);
      this.ctx.lineTo(e.x + e.w * 0.9, e.y + e.h * 0.7);
      this.ctx.lineTo(e.x + e.w * 0.85, e.y + e.h * 0.6);
      this.ctx.lineTo(e.x + e.w * 0.9, e.y + e.h * 0.5);
      this.ctx.lineTo(e.x + e.w * 0.85, e.y + e.h * 0.4);
      this.ctx.lineTo(e.x + e.w * 0.9, e.y + e.h * 0.3);
      this.ctx.lineTo(e.x + e.w * 0.8, e.y + e.h * 0.2);
      this.ctx.lineTo(e.x + e.w * 0.7, e.y);
      this.ctx.closePath();
      this.ctx.fill();
      
      // Farlige røde øyne
      this.ctx.fillStyle = '#ff3232';
      this.ctx.beginPath();
      this.ctx.arc(e.x + e.w * 0.25, e.y + e.h * 0.35, e.w * 0.08, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.beginPath();
      this.ctx.arc(e.x + e.w * 0.75, e.y + e.h * 0.35, e.w * 0.08, 0, Math.PI * 2);
      this.ctx.fill();
      
      // Edderkopp-detaljer - antenner (enkle linjer)
      this.ctx.strokeStyle = '#ffffff';
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      this.ctx.moveTo(e.x + e.w * 0.25, e.y + e.h * 0.1);
      this.ctx.lineTo(e.x + e.w * 0.2, e.y + e.h * 0.05);
      this.ctx.moveTo(e.x + e.w * 0.75, e.y + e.h * 0.1);
      this.ctx.lineTo(e.x + e.w * 0.8, e.y + e.h * 0.05);
      this.ctx.stroke();
      
      // Farlige klør
      this.ctx.beginPath();
      this.ctx.moveTo(e.x + e.w * 0.1, e.y + e.h * 0.4);
      this.ctx.lineTo(e.x + e.w * 0.05, e.y + e.h * 0.35);
      this.ctx.moveTo(e.x + e.w * 0.9, e.y + e.h * 0.4);
      this.ctx.lineTo(e.x + e.w * 0.95, e.y + e.h * 0.35);
      this.ctx.stroke();
    });
  }

  drawBullets(bullets, isEnemy = false) {
    bullets.forEach(b => {
      if (isEnemy) {
        this.ctx.fillStyle = '#fca5a5';
      } else {
        this.ctx.fillStyle = '#a78bfa';
      }
      this.ctx.fillRect(b.x - b.r, b.y - b.r, b.r * 2, b.r * 2);
    });
  }

  drawPlayer(player) {
    // Enkel spiller
    this.ctx.fillStyle = '#60a5fa';
    this.ctx.fillRect(player.x, player.y, player.w, player.h);
    
    // Enkel cockpit
    this.ctx.fillStyle = '#ffffff';
    this.ctx.fillRect(player.x + player.w * 0.36, player.y + 2, player.w * 0.28, player.h * 0.6);
  }

  drawUI() {
    // Fjernet UI overlay for ytelse
  }

  drawGameOver() {
    const W = this.canvas.width / (window.devicePixelRatio || 1);
    const H = this.canvas.height / (window.devicePixelRatio || 1);
    
    this.ctx.fillStyle = 'rgba(0,0,0,0.7)';
    this.ctx.fillRect(0, 0, W, H);
    this.ctx.fillStyle = '#ffffff';
    this.ctx.textAlign = 'center';
    this.ctx.font = '28px Arial, sans-serif';
    this.ctx.fillText('Game Over', W / 2, H / 2);
  }

  render(gameState) {
    if (!gameState || !gameState.barriers || !gameState.enemies) {
      // Game not initialized yet, just draw background
      this.drawBackground();
      return;
    }
    
    this.drawBackground();
    this.drawBarriers(gameState.barriers);
    this.drawEnemies(gameState.enemies);
    this.drawBullets(gameState.enemyBullets, true);
    this.drawPlayer(gameState.player);
    this.drawBullets(gameState.bullets, false);
    this.drawUI();
    
    if (gameState.gameOver) {
      this.drawGameOver();
    }
  }
}
